import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { 
  Box, 
  Typography, 
  Grid,
  Alert,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert as MuiAlert,
  Autocomplete,
  Divider,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from '@mui/material';
import { GiAmbulance, GiTeacher, GiPerson } from 'react-icons/gi';
import { FaHandsHelping, FaUserClock, FaPhone, FaHospital } from 'react-icons/fa';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebaseConfig';
import { collection, getDocs, query, where, doc, setDoc, getDoc } from 'firebase/firestore';
import Slider from '@mui/material/Slider';
import { CheckCircle, Cancel, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';

// Styled Card with rounded corners and modern blue color
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: '20px',
  background: 'linear-gradient(160deg, #2563eb 0%, #1e40af 100%)',
  color: 'white',
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  boxShadow: '0 10px 30px rgba(37, 99, 235, 0.25)',
  border: '1px solid rgba(255,255,255,0.18)',
  backdropFilter: 'saturate(120%) blur(2px)',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 14px 36px rgba(30, 64, 175, 0.35)',
  },
}));

const MonthlyCard = styled(Card)(({ theme, status }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  borderRadius: '20px',
  background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)',
  transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.06)',
  border: `2px solid ${
    status === 'Green' ? theme.palette.success.light :
    status === 'Yellow' ? theme.palette.warning.light :
    theme.palette.error.light
  }`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.1)',
  },
}));

const StatusIndicator = styled(Box)(({ theme, status }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1, 2),
  borderRadius: '12px',
  backgroundColor: 
    status === 'Green' ? 'rgba(46, 125, 50, 0.08)' :
    status === 'Yellow' ? 'rgba(237, 108, 2, 0.08)' :
    'rgba(211, 47, 47, 0.08)',
  color: 
    status === 'Green' ? theme.palette.success.main :
    status === 'Yellow' ? theme.palette.warning.main :
    theme.palette.error.main,
  fontWeight: 600,
  marginTop: theme.spacing(1),
}));

const IconWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '3.25rem',
  marginBottom: theme.spacing(2),
  color: 'white',
  background: 'rgba(255, 255, 255, 0.12)',
  borderRadius: '50%',
  padding: theme.spacing(2),
  backdropFilter: 'blur(5px)',
}));

const StatusBanner = styled(Alert)(({ theme, status }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  padding: theme.spacing(2),
  backgroundColor: 
    status === 'Green' ? 'rgba(46, 125, 50, 0.06)' :
    status === 'Yellow' ? 'rgba(237, 108, 2, 0.06)' :
    'rgba(211, 47, 47, 0.06)',
  color: 
    status === 'Green' ? theme.palette.success.main :
    status === 'Yellow' ? theme.palette.warning.main :
    theme.palette.error.main,
  border: `1px solid ${
    status === 'Green' ? theme.palette.success.main :
    status === 'Yellow' ? theme.palette.warning.main :
    theme.palette.error.main
  }`,
  '& .MuiAlert-icon': {
    color: 
      status === 'Green' ? theme.palette.success.main :
      status === 'Yellow' ? theme.palette.warning.main :
      theme.palette.error.main,
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

// Modern buttons
const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 700,
  letterSpacing: 0.2,
  background: 'linear-gradient(160deg, #1d4ed8 0%, #1e40af 100%)',
  color: '#fff',
  boxShadow: '0 6px 18px rgba(37, 99, 235, 0.25)',
  '&:hover': {
    background: 'linear-gradient(160deg, #1e40af 0%, #1e3a8a 100%)',
    boxShadow: '0 10px 24px rgba(30, 64, 175, 0.32)',
  },
  '&:focus-visible': {
    outline: '3px solid rgba(59,130,246,0.35)',
    outlineOffset: '2px',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 700,
  letterSpacing: 0.2,
  color: '#0f172a',
  background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)',
  border: '1px solid #e2e8f0',
  boxShadow: '0 4px 12px rgba(2, 6, 23, 0.06)',
  '&:hover': {
    background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
    boxShadow: '0 8px 20px rgba(2, 6, 23, 0.08)',
  },
  '&:focus-visible': {
    outline: '3px solid rgba(15,23,42,0.25)',
    outlineOffset: '2px',
  },
}));

const DashboardContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
  minHeight: '100vh',
  padding: theme.spacing(1),
  maxWidth: '100%',
  margin: '0 auto',
  width: '100%',
  overflow: 'hidden',
  boxSizing: 'border-box',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
    maxWidth: '1200px',
  },
}));

const DashboardHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  background: '#ffffff',
  borderRadius: '16px',
  padding: theme.spacing(1.5),
  boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  boxSizing: 'border-box',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2.5),
  },
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  boxSizing: 'border-box',
  '& .MuiButton-root': {
    flex: '1 1 auto',
    minWidth: '120px',
    maxWidth: '200px',
    borderRadius: '12px',
    padding: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1.5),
      fontSize: '1rem',
    },
  },
}));

const MonthlyTilesContainer = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    padding: theme.spacing(2),
  },
}));

// Function to fetch users from Firestore
const getUsersFromFirestore = async () => {
  const usersCollectionRef = collection(db, 'users'); // Adjust the collection name as needed
  const usersSnapshot = await getDocs(usersCollectionRef);
  const usersList = usersSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
  return usersList;
};

// New Sidebar component
const UserSidebar = ({ users, open, setOpen }) => {
  // Custom sorting function for alphanumeric display names
  const alphanumericSort = (a, b) => {
    // Handle cases where displayName is null/undefined
    if (!a.displayName || !b.displayName) {
      return (a.displayName || '').localeCompare(b.displayName || '');
    }

    const regex = /(\D+)(\d+)/; // Regex to separate non-numeric and numeric parts
    const aMatch = a.displayName.match(regex);
    const bMatch = b.displayName.match(regex);

    // If both names match the pattern, use alphanumeric sorting
    if (aMatch && bMatch) {
      // Compare non-numeric parts first
      const nonNumericComparison = aMatch[1].localeCompare(bMatch[1]);
      if (nonNumericComparison !== 0) return nonNumericComparison;

      // If non-numeric parts are equal, compare numeric parts
      return parseInt(aMatch[2], 10) - parseInt(bMatch[2], 10);
    }

    // If only one name matches the pattern, put the non-matching one first
    if (aMatch && !bMatch) return 1;
    if (!aMatch && bMatch) return -1;

    // If neither matches the pattern, use simple string comparison
    return a.displayName.localeCompare(b.displayName);
  };

  // Sort users by display name using the custom sorting function
  const sortedUsers = [...users].sort(alphanumericSort);

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          width: 250,
          bgcolor: 'rgba(255, 255, 255, 0.92)', // Light background for modern look
          padding: 2,
          borderRight: '2px solid #e5e7eb', // Softer vertical bar
          height: '100vh', // Full height of the viewport
          boxShadow: 3, // Add shadow for depth
          borderRadius: 2, // Rounded corners
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', top: 10, right: 10 }}>
        <ChevronLeft />
      </IconButton>
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 1, 
          fontWeight: 'bold', 
          textAlign: 'center', 
          color: '#2563eb', // Modern blue color
        }} 
      >
        Availability
      </Typography>
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}> {/* Make this box scrollable */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Available</Typography>
        <List>
          {sortedUsers.filter(user => user.available).map((user, index) => (
            <Tooltip 
              key={index} 
              title={
                <span>
                  Phone: <a href={`tel:${user.cellNumber}`}>{user.cellNumber}</a><br />
                  Type: {user.certLevel}
                </span>
              } // Tooltip content with clickable phone number
              arrow
              placement="right" // Position the tooltip to the right
            >
              <ListItem sx={{ '&:hover': { backgroundColor: '#f3f4f6' } }}> {/* Hover effect */}
                <ListItemText primary={user.displayName} />
                <CheckCircle sx={{ color: 'green' }} /> {/* Green check for available users */}
              </ListItem>
            </Tooltip>
          ))}
        </List>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Unavailable</Typography>
        <List>
          {sortedUsers.filter(user => !user.available).map((user, index) => (
            <Tooltip 
              key={index} 
              title={
                <span>
                  Phone: <a href={`tel:${user.phoneNumber}`}>{user.phoneNumber}</a><br />
                  Type: {user.type}
                </span>
              } // Tooltip content with clickable phone number
              arrow
              placement="right" // Position the tooltip to the right
            >
              <ListItem sx={{ '&:hover': { backgroundColor: '#f3f4f6' } }}> {/* Hover effect */}
                <ListItemText primary={user.displayName} />
                <Cancel sx={{ color: 'red' }} /> {/* Red X for unavailable users */}
              </ListItem>
            </Tooltip>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

const Dashboard = () => {
  const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format

  const [callPoints, setCallPoints] = useState(0);
  const [classPoints, setClassPoints] = useState(0);
  const [skillPoints, setSkillPoints] = useState(0);
  const [volunteerHours, setVolunteerHours] = useState(0);
  const [status, setStatus] = useState('');
  const [openCallDialog, setOpenCallDialog] = useState(false);
  const [role, setRole] = useState('');
  const [roles, setRoles] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const isFetchingCallsRef = useRef(false);
  const isFetchingSkillsRef = useRef(false);
  const isFetchingClassesRef = useRef(false);
  const isFetchingVolunteerRef = useRef(false);

  // Log Activity state variables
  const [openSkillDialog, setOpenSkillDialog] = useState(false);
  const [openClassDialog, setOpenClassDialog] = useState(false);
  const [openVolunteerDialog, setOpenVolunteerDialog] = useState(false);
  const [skillDate, setSkillDate] = useState(today); // Set initial state to today's date
  const [skillName, setSkillName] = useState('');
  const [practicePartner, setPracticePartner] = useState('');
  const [practiceHours, setPracticeHours] = useState(0);
  const [classes, setClasses] = useState([]);
  const [volunteerDate, setVolunteerDate] = useState(today); // Set initial state to today's date
  const [volunteerActivity, setVolunteerActivity] = useState('');
  const [volunteerTime, setVolunteerTime] = useState(0.5);
  const [date, setDate] = useState(today); // Set initial state to today's date
  const [classDate, setClassDate] = useState(today); // Set initial state to today's date
  const [selectedClass, setSelectedClass] = useState('');
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);

  // State to hold monthly data
  const [monthlyData, setMonthlyData] = useState([]);
  const [overallStatus, setOverallStatus] = useState(''); // State for overall status

  const [email, setEmail] = useState('');
  const [unitNumber, setUnitNumber] = useState('');

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openBusDialog, setOpenBusDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [busNumber, setBusNumber] = useState('');

  const [users, setUsers] = useState([]); // State to hold user data

  const [sidebarOpen, setSidebarOpen] = useState(false); // State to control sidebar visibility

  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [detailsData, setDetailsData] = useState([]);
  const [detailsTitle, setDetailsTitle] = useState('');
  const [detailsType, setDetailsType] = useState('');

  const [openPieChartDialog, setOpenPieChartDialog] = useState(false);
  const [pieChartData, setPieChartData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [showChart, setShowChart] = useState(true);
  const [selectedChartSection, setSelectedChartSection] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        setEmail(user.email);
        // Fetch user data to get unit number
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUnitNumber(userData.unitNumber || '');
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    });
  }, []);

  useEffect(() => {
    if (email) {
      fetchCurrentUserSkills();
      fetchCurrentUserCalls();
      fetchCurrentUserClasses();
      fetchCurrentUserVolunteerHours();
    }
  }, [email]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const classesDocRef = doc(db, 'hvac/GenericData/Classes/Classes',);
        const classesDocSnapshot = await getDoc(classesDocRef);

        if (classesDocSnapshot.exists()) {
          const classesData = classesDocSnapshot.data();
          setClasses(classesData.classes);
        } else {
          console.error('No such document for classes!');
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
        setSnackbar({ open: true, message: 'Error fetching classes.', severity: 'error' });
      }
    };

    fetchClasses();
  }, []);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const providersDocRef = doc(db, 'hvac/GenericData/Providers', 'ProvidersList');
        const providersDocSnapshot = await getDoc(providersDocRef);

        if (providersDocSnapshot.exists()) {
          const providersData = providersDocSnapshot.data();

          const alsProviders = Array.isArray(providersData.ALS) ? providersData.ALS : [];
          const blsProviders = Array.isArray(providersData.BLS) ? providersData.BLS : [];

          const combinedProviders = [...alsProviders, ...blsProviders];
          const sortedProviders = combinedProviders.sort();
          console.log('Providers List:', sortedProviders);
          setProviders(sortedProviders);
        } else {
          console.error('No such document for providers!');
        }
      } catch (error) {
        console.error('Error fetching providers:', error);
        setSnackbar({ open: true, message: 'Error fetching providers.', severity: 'error' });
      }
    };

    fetchProviders();
  }, []);

  useEffect(() => {
    const fetchAllMonthlyData = async () => {
      const currentYear = new Date().getFullYear();
      const data = await Promise.all(months.map((_, index) => fetchMonthlyData(index, currentYear)));
      setMonthlyData(data);
      calculateOverallStatus(data); // Calculate overall status after fetching monthly data
    };

    fetchAllMonthlyData(); // Initial fetch on page load
  }, [email]); // Only run on page load and when email changes

  const calculateOverallStatus = (data) => {
    // Sum up the points from all months
    const totalCalls = data.reduce((acc, month) => acc + month.callsPoints, 0);
    const totalSkills = data.reduce((acc, month) => acc + month.skillsPoints, 0);
    const totalClasses = data.reduce((acc, month) => acc + month.classesPoints, 0);
    const totalVolunteers = data.reduce((acc, month) => acc + month.volunteerPoints, 0);

    // Calculate overall status
    const status = calculateStatus(totalCalls, totalSkills, totalClasses, totalVolunteers);
    setOverallStatus(status); // Update the overall status state
  };

  const fetchCurrentUserClasses = async () => {
    if (isFetchingClassesRef.current) return;
    isFetchingClassesRef.current = true;

    const currentDate = new Date();
    const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const currentYear = currentDate.getFullYear();

    const classesRef = collection(db, 'hvac', 'ClassData', 'Responses');
    const q = query(classesRef, where('Email', '==', email));

    try {
      const querySnapshot = await getDocs(q);
      let totalClasses = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const dateString = data.Date;
        const [year, month] = dateString.split('-');

        if (month === currentMonth && year === currentYear.toString()) {
          totalClasses += 1;
        }
      });

      setClassPoints(totalClasses);
    } catch (error) {
      console.error('Error fetching class data:', error);
    } finally {
      isFetchingClassesRef.current = false;
    }
  };

  const fetchCurrentUserCalls = async () => {
    if (isFetchingCallsRef.current) return;
    isFetchingCallsRef.current = true;

    const callsRef = collection(db, 'hvac', 'CallData', 'Responses');
    const q = query(callsRef, where('Email', '==', email));

    try {
      const querySnapshot = await getDocs(q);
      let totalCalls = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.Date) return;

        const dateString = data.Date;
        const [year, month] = dateString.split('-');
        const currentDate = new Date();
        const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
        const currentYear = currentDate.getFullYear().toString();

        if (month === currentMonth && year === currentYear) {
          totalCalls += 1;
        }
      });

      setCallPoints(totalCalls);
    } catch (error) {
      console.error('Error fetching call data:', error);
    } finally {
      isFetchingCallsRef.current = false;
    }
  };

  const fetchCurrentUserSkills = async () => {
    if (isFetchingSkillsRef.current) return;
    isFetchingSkillsRef.current = true;

    const currentDate = new Date();
    const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const currentYear = currentDate.getFullYear().toString();

    const skillsRef = collection(db, 'hvac', 'SkillData', 'Responses');
    const q = query(skillsRef, where('Email', '==', email));

    try {
      const querySnapshot = await getDocs(q);
      let totalPracticeHours = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.Date) return;

        const dateString = data.Date;
        const [year, month] = dateString.split('-');

        if (month === currentMonth && year === currentYear) {
          totalPracticeHours += data.PracticeHours || 0;
        }
      });

      setSkillPoints(totalPracticeHours);
    } catch (error) {
      console.error('Error fetching skill data:', error);
    } finally {
      isFetchingSkillsRef.current = false;
    }
  };

  const fetchCurrentUserVolunteerHours = async () => {
    if (isFetchingVolunteerRef.current) return;
    isFetchingVolunteerRef.current = true;

    const currentDate = new Date();
    const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const currentYear = currentDate.getFullYear().toString();

    const volunteerRef = collection(db, 'hvac', 'VolunteerData', 'Responses');
    const q = query(volunteerRef, where('Email', '==', email));

    try {
      const querySnapshot = await getDocs(q);
      let totalVolunteerHours = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.Date || typeof data.Time !== 'number') return;

        const dateString = data.Date;
        const [year, month] = dateString.split('-');

        if (month === currentMonth && year === currentYear) {
          totalVolunteerHours += data.Time;
        }
      });

      console.log('Total volunteer hours:', totalVolunteerHours);
      setVolunteerHours(totalVolunteerHours);
    } catch (error) {
      console.error('Error fetching volunteer hours data:', error);
    } finally {
      isFetchingVolunteerRef.current = false;
    }
  };

  const handleCallSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
        setSnackbar({ open: true, message: 'User not authenticated.', severity: 'error' });
        return;
    }

    try {
        const collectionRef = collection(db, 'hvac', 'CallData', 'Responses');
        const docId = new Date().toISOString();
        const docRef = doc(collectionRef, docId);

        const callData = {
            Date: date,
            Role: role,
            Email: user.email,
            EntryType: 'Call',
        };

        await setDoc(docRef, callData);
        setSnackbar({ open: true, message: 'Call logged successfully!', severity: 'success' });
        resetCallForm();
        setOpenCallDialog(false);
        // Refresh data after successful submission
        await fetchCurrentUserCalls();
        // Refresh monthly data
        const currentYear = new Date().getFullYear();
        const data = await Promise.all(months.map((_, index) => fetchMonthlyData(index, currentYear)));
        setMonthlyData(data);
        calculateOverallStatus(data);
    } catch (error) {
        console.error('Error adding call document: ', error);
        setSnackbar({ open: true, message: 'Error logging call: ' + error.message, severity: 'error' });
    }
  };

  const resetCallForm = () => {
    setDate('');
    setRole('');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Skill submission handler
  const handleSkillSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      setSnackbar({ open: true, message: 'User not authenticated.', severity: 'error' });
      return;
    }

    try {
      const collectionRef = collection(db, 'hvac', 'SkillData', 'Responses');
      const docId = new Date().toISOString();
      const docRef = doc(collectionRef, docId);

      await setDoc(docRef, {
        Date: skillDate,
        SkillName: skillName,
        PracticePartner: selectedProvider,
        PracticeHours: practiceHours,
        Email: user.email,
        EntryType: 'Skill',
      });

      setSnackbar({ open: true, message: 'Skill logged successfully!', severity: 'success' });
      resetSkillForm();
      setOpenSkillDialog(false);
      // Refresh data after successful submission
      await fetchCurrentUserSkills();
      // Refresh monthly data
      const currentYear = new Date().getFullYear();
      const data = await Promise.all(months.map((_, index) => fetchMonthlyData(index, currentYear)));
      setMonthlyData(data);
      calculateOverallStatus(data);
    } catch (error) {
      console.error('Error adding skill document: ', error);
      setSnackbar({ open: true, message: 'Error logging skill.', severity: 'error' });
    }
  };

  const resetSkillForm = () => {
    setSkillDate('');
    setSkillName('');
    setPracticePartner('');
    setPracticeHours(0);
  };

  // Class submission handler
  const handleClassSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    const user = auth.currentUser;
    try {
      const classesRef = collection(db, 'hvac', 'ClassData', 'Responses');
      const docId = new Date().toISOString();
      const docRef = doc(classesRef, docId);

      await setDoc(docRef, {
        Date: classDate,
        ClassName: selectedClass,
        Email: user.email,
        EntryType: 'Class',
      });

      setSnackbar({ open: true, message: 'Class logged successfully!', severity: 'success' });
      resetClassForm();
      setOpenClassDialog(false);
      // Refresh data after successful submission
      await fetchCurrentUserClasses();
      // Refresh monthly data
      const currentYear = new Date().getFullYear();
      const data = await Promise.all(months.map((_, index) => fetchMonthlyData(index, currentYear)));
      setMonthlyData(data);
      calculateOverallStatus(data);
    } catch (error) {
      console.error('Error adding class document: ', error);
      setSnackbar({ open: true, message: 'Error logging class.', severity: 'error' });
    }
  };

  const resetClassForm = () => {
    setClassDate('');
    setSelectedClass('');
  };


  // Volunteer submission handler
  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
        setSnackbar({ open: true, message: 'User not authenticated.', severity: 'error' });
        return;
    }

    try {
        const collectionRef = collection(db, 'hvac', 'VolunteerData', 'Responses');
        const docId = new Date().toISOString();
        const docRef = doc(collectionRef, docId);

        await setDoc(docRef, {
            Date: volunteerDate,
            Activity: volunteerActivity,
            Time: volunteerTime,
            Email: user.email,
            EntryType: 'Volunteer',
        });

        setSnackbar({ open: true, message: 'Volunteer hours logged successfully!', severity: 'success' });
        resetVolunteerForm();
        setOpenVolunteerDialog(false);
        // Refresh data after successful submission
        await fetchCurrentUserVolunteerHours();
        // Refresh monthly data
        const currentYear = new Date().getFullYear();
        const data = await Promise.all(months.map((_, index) => fetchMonthlyData(index, currentYear)));
        setMonthlyData(data);
        calculateOverallStatus(data);
    } catch (error) {
        console.error('Error adding volunteer document: ', error);
        setSnackbar({ open: true, message: 'Error logging volunteer hours: ' + error.message, severity: 'error' });
    }
  };

  // Reset volunteer form
  const resetVolunteerForm = () => {
    setVolunteerDate('');
    setVolunteerActivity('');
    setVolunteerTime(0.5);
  };

  const months = [
    { name: 'January', status: 'Green' },
    { name: 'February', status: 'Yellow' },
    { name: 'March', status: 'Red' },
    { name: 'April', status: 'Green' },
    { name: 'May', status: 'Yellow' },
    { name: 'June', status: 'Red' },
    { name: 'July', status: 'Green' },
    { name: 'August', status: 'Yellow' },
    { name: 'September', status: 'Red' },
    { name: 'October', status: 'Green' },
    { name: 'November', status: 'Yellow' },
    { name: 'December', status: 'Red' },
  ];

  const calculateStatus = (callsPoints, skillsPoints, classesPoints, volunteerPoints) => {
    const totalCalls = callsPoints + (volunteerPoints >= 1 ? 1 : 0); // Count nighttime as an additional call if applicable
    const totalActivities = skillsPoints + classesPoints + volunteerPoints + callsPoints;

    // For skills, we consider 1 hour of practice as meeting the requirement
    const hasSkills = skillsPoints >= 1;

    if (callsPoints >= 8 && hasSkills && classesPoints >= 1 && totalActivities >= 10) {
        return 'Green';
    } else if (callsPoints >= 8 && totalActivities >= 7 && (hasSkills || classesPoints >= 1 || volunteerPoints >= 1)) {
        return 'Yellow';
    } else {
        return 'Red';
    }
  };

  const fetchMonthlyData = async (month, year) => {
    const monthString = (month + 1).toString().padStart(2, '0'); // Convert month index to MM format
    const yearString = year.toString();

    const callsRef = collection(db, 'hvac', 'CallData', 'Responses');
    const skillsRef = collection(db, 'hvac', 'SkillData', 'Responses');
    const classesRef = collection(db, 'hvac', 'ClassData', 'Responses');
    const volunteerRef = collection(db, 'hvac', 'VolunteerData', 'Responses');

    const fetchData = async (ref, entryType = null) => {
      try {
        const q = query(ref, where('Email', '==', email));
        const querySnapshot = await getDocs(q);
        let totalPoints = 0;
        let nighttimeCalls = 0;
        let firstNighttimeCallCounted = false;

        // Sort the documents by date
        const sortedDocs = querySnapshot.docs.sort((a, b) => {
          const dateA = new Date(a.data().Date);
          const dateB = new Date(b.data().Date);
          return dateA - dateB;
        });

        sortedDocs.forEach((doc) => {
          const data = doc.data();
          if (!data.Date) return;

          const dateString = data.Date;
          const [year, month] = dateString.split('-');

          if (month === monthString && year === yearString) {
            if (entryType === 'Call' && data.Role === 'Nighttime') {
              nighttimeCalls += 1;

              if (!firstNighttimeCallCounted) {
                totalPoints += 2;  // Add two points for the first nighttime call
                firstNighttimeCallCounted = true;
              } else {
                totalPoints += 1; // Add one point for subsequent nighttime calls
              }
            } else if (entryType === 'Skill') {
              totalPoints += data.PracticeHours || 0; // Sum up practice hours for skills
            } else if (entryType === 'Volunteer') {
              totalPoints += data.Time || 0; // Sum up volunteer hours
            } else {
              totalPoints += 1; // Add one point for all other entries
            }
          }
        });

        return { totalPoints, nighttimeCalls };
      } catch (error) {
        console.error('Error fetching data:', error);
        return { totalPoints: 0, nighttimeCalls: 0 };
      }
    };

    const { totalPoints: callsPoints, nighttimeCalls } = await fetchData(callsRef, 'Call');
    const { totalPoints: skillsPoints } = await fetchData(skillsRef, 'Skill');
    const { totalPoints: classesPoints } = await fetchData(classesRef);
    const { totalPoints: volunteerPoints } = await fetchData(volunteerRef, 'Volunteer');

    // Calculate status based on the points
    const status = calculateStatus(callsPoints, skillsPoints, classesPoints, volunteerPoints);

    return { callsPoints, skillsPoints, classesPoints, volunteerPoints, status };
  };


  const handleMonthClick = (monthData, monthName) => {
    const { callsPoints, skillsPoints, classesPoints, volunteerPoints } = monthData;
    
    const data = [
      { name: 'Calls', value: callsPoints || 0 },
      { name: 'Skills', value: skillsPoints || 0 },
      { name: 'Classes', value: classesPoints || 0 },
      { name: 'Volunteer', value: volunteerPoints || 0 }
    ];

    setPieChartData(data);
    setSelectedMonth(monthName);
    setOpenPieChartDialog(true);
  };

  const handlePieChartClick = (entry) => {
    setSelectedChartSection(entry.name);
    setShowChart(false);
  };

  const handleClosePieChart = () => {
    setOpenPieChartDialog(false);
    setPieChartData([]);
    setSelectedMonth('');
    setShowChart(true);
    setSelectedChartSection(null);
  };

  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444'];

  const renderMonthlyTiles = () => {
    const currentYear = new Date().getFullYear().toString();
    
    return monthlyData.map((data, index) => {
      const { callsPoints, skillsPoints, classesPoints, volunteerPoints, status } = data;
      const monthName = months[index].name;

      // Check if there's any data for this month
      const hasData = [callsPoints, skillsPoints, classesPoints, volunteerPoints].some(
        point => point !== null && point !== undefined && point > 0
      );

      if (!hasData) return null;

      return (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <MonthlyCard 
            status={status}
            onClick={() => handleMonthClick(data, monthName)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#1a237e' }}>
              {monthName}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <GiAmbulance style={{ marginRight: '8px', color: '#007BFF' }} />
                Calls: {callsPoints || 0}
              </Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FaHandsHelping style={{ marginRight: '8px', color: '#007BFF' }} />
                Skills: {skillsPoints || 0}
              </Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <GiTeacher style={{ marginRight: '8px', color: '#007BFF' }} />
                Classes: {classesPoints || 0}
              </Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FaUserClock style={{ marginRight: '8px', color: '#007BFF' }} />
                Volunteer: {volunteerPoints || 0}
              </Typography>
            </Box>
            <StatusIndicator status={status}>
              Status: {status}
            </StatusIndicator>
          </MonthlyCard>
        </Grid>
      );
    }).filter(Boolean);
  };

  // Fetch roles from Firestore
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesDocRef = doc(db, 'hvac/CallDropdown/Roles', 'CallRoles'); // Accessing the CallRoles document
        const rolesDocSnapshot = await getDoc(rolesDocRef);

        if (rolesDocSnapshot.exists()) {
          const rolesData = rolesDocSnapshot.data(); // Access the data
          setRoles(rolesData.Test); // Assuming 'Test' contains the array of roles
        } else {
          console.error('No such document for roles!');
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        setSnackbar({ open: true, message: 'Error fetching roles.', severity: 'error' });
      }
    };

    fetchRoles();
  }, []); // Fetch roles on component mount // Run once on component mount

  const fetchData = async () => {
    if (email) {
      await fetchCurrentUserSkills();
      await fetchCurrentUserCalls();
      await fetchCurrentUserClasses();
      await fetchCurrentUserVolunteerHours();
    }
  };

  useEffect(() => {
    fetchData(); // Initial fetch on page load
  }, [email]); // Only run on page load and when email changes

  const handleRoleChange = (e) => {
    const role = e.target.value;
    setSelectedRole(role);

    if (role === 'Primary') {
      setOpenConfirmDialog(true);
    } else if (role === 'Driver') {
      setOpenBusDialog(true);
    } else {
      setRole(role); // Set the role directly for other options
    }
  };

  const handleConfirmSubmit = () => {
    setRole(selectedRole);
    setOpenConfirmDialog(false);
  };

  const handleBusSubmit = () => {
    if (busNumber) {
      setRole('Driver'); // Set the role to Driver
      // You can also store the bus number in a state if needed
      setOpenBusDialog(false);
      setBusNumber(''); // Reset bus number
    } else {
      // Optionally handle the case where no bus number is provided
      alert("Please enter a bus number.");
    }
  };

  function calculatePoints(data) {
    const {
        primaryCalls,
        nonPrimaryTransportCalls,
        nonPrimaryRmaCalls,
        nighttimeCalls,
        ceClasses,
        skillReviewHours,
        volunteerHours
    } = data;

    // Calculate points for Call Volume
    let callVolumePoints = primaryCalls + nonPrimaryTransportCalls + nonPrimaryRmaCalls + (nighttimeCalls * 2);
    callVolumePoints = Math.min(callVolumePoints, 9); // Limit to 9 points per month

    // Calculate points for Continuing Education (CE)
    let cePoints = ceClasses;
    cePoints = Math.min(cePoints, 2); // Limit to 2 points per month

    // Calculate points for Skill Review based on hours spent
    let skillReviewPoints = Math.min(skillReviewHours, 4); // Limit to 4 points per month

    // Calculate points for Volunteering based on hours spent
    let volunteerPoints = Math.min(volunteerHours * 0.5, 3); // Limit to 3 points per month

    // Total points
    const totalPoints = callVolumePoints + cePoints + skillReviewPoints + volunteerPoints;

    return {
        callVolumePoints,
        cePoints,
        skillReviewPoints,
        volunteerPoints,
        totalPoints
    };
  }

  function calculatePointsForCurrentMonth(data) {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    // Filter data for the current month
    const currentMonthData = data.filter(entry => {
        const entryDate = new Date(entry.date); // Assuming each entry has a 'date' field
        return entryDate.getMonth() === currentMonth && entryDate.getFullYear() === currentYear;
    });

    // Aggregate data for the current month
    const aggregatedData = currentMonthData.reduce((acc, entry) => {
        acc.primaryCalls += entry.primaryCalls || 0;
        acc.nonPrimaryTransportCalls += entry.nonPrimaryTransportCalls || 0;
        acc.nonPrimaryRmaCalls += entry.nonPrimaryRmaCalls || 0;
        acc.nighttimeCalls += entry.nighttimeCalls || 0;
        acc.ceClasses += entry.ceClasses || 0;
        acc.skillReviewHours += entry.skillReviewHours || 0;
        acc.volunteerHours += entry.volunteerHours || 0;
        return acc;
    }, {
        primaryCalls: 0,
        nonPrimaryTransportCalls: 0,
        nonPrimaryRmaCalls: 0,
        nighttimeCalls: 0,
        ceClasses: 0,
        skillReviewHours: 0,
        volunteerHours: 0
    });

    // Calculate points using the aggregated data
    const points = calculatePoints(aggregatedData);

    // Determine RMI score and status color
    let statusColor = 'red'; // Default to red

    if (points.totalPoints >= 11) {
        statusColor = 'green'; // Minimum level of proficiency
    } else if (points.totalPoints >= 8) {
        statusColor = 'yellow'; // Falls short of the minimum level
    }

    // Check minimum expectations
    if (points.callVolumePoints < 8 || points.cePoints < 1 || points.skillReviewPoints < 1) {
        statusColor = 'red'; // Does not meet the criteria
    }

    return {
        ...points,
        statusColor
    };
  }

  // Add a new function to get the current month's status
  const getCurrentMonthStatus = () => {
    const currentMonthIndex = new Date().getMonth();
    return monthlyData[currentMonthIndex]?.status || 'Red'; // Default to 'Red' if no data
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersList = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersList);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();

    // Add event listener for availability changes
    const handleAvailabilityChange = () => {
      fetchUsers(); // Refresh the users list
    };

    window.addEventListener('availabilityChanged', handleAvailabilityChange);

    // Cleanup
    return () => {
      window.removeEventListener('availabilityChanged', handleAvailabilityChange);
    };
  }, []);

  const handleCardClick = async (type) => {
    try {
      let collectionRef;
      let title;
      let data = [];

      switch (type) {
        case 'calls':
          collectionRef = collection(db, 'hvac', 'CallData', 'Responses');
          title = 'Call Details';
          break;
        case 'skills':
          collectionRef = collection(db, 'hvac', 'SkillData', 'Responses');
          title = 'Skill Practice Details';
          break;
        case 'classes':
          collectionRef = collection(db, 'hvac', 'ClassData', 'Responses');
          title = 'Class Details';
          break;
        case 'volunteer':
          collectionRef = collection(db, 'hvac', 'VolunteerData', 'Responses');
          title = 'Volunteer Details';
          break;
        default:
          return;
      }

      const q = query(collectionRef, where('Email', '==', email));
      const querySnapshot = await getDocs(q);

      const currentDate = new Date();
      const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
      const currentYear = currentDate.getFullYear().toString();

      if (type === 'calls') {
        // For calls, create role distribution data and collect table data
        const roleCounts = {};
        const tableData = [];

        querySnapshot.forEach((doc) => {
          const entryData = doc.data();
          if (entryData.Date) {
            const [year, month] = entryData.Date.split('-');
            if (month === currentMonth && year === currentYear) {
              const role = entryData.Role || 'Unknown';
              roleCounts[role] = (roleCounts[role] || 0) + 1;
              tableData.push(entryData);
            }
          }
        });

        // Convert role counts to pie chart data format
        const pieData = Object.entries(roleCounts).map(([name, value]) => ({
          name,
          value
        }));

        // Sort table data by date
        tableData.sort((a, b) => new Date(b.Date) - new Date(a.Date));

        setPieChartData(pieData);
        setDetailsData(tableData);
        setDetailsTitle(title);
        setDetailsType(type);
        setSelectedMonth('Current Month');  
        setOpenPieChartDialog(true);
      } else {
        // For other types, show the table view
        querySnapshot.forEach((doc) => {
          const entryData = doc.data();
          if (entryData.Date) {
            const [year, month] = entryData.Date.split('-');
            if (month === currentMonth && year === currentYear) {
              data.push(entryData);
            }
          }
        });

        data.sort((a, b) => new Date(b.Date) - new Date(a.Date));
        setDetailsData(data);
        setDetailsTitle(title);
        setDetailsType(type);
        setOpenDetailsDialog(true);
      }
    } catch (error) {
      console.error('Error fetching details:', error);
      setSnackbar({ open: true, message: 'Error fetching details.', severity: 'error' });
    }
  };

  const handleCloseDetails = () => {
    setOpenDetailsDialog(false);
    setDetailsData([]);
    setDetailsTitle('');
    setDetailsType('');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <UserSidebar users={users} open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <IconButton 
        onClick={() => setSidebarOpen(!sidebarOpen)} 
        sx={{ 
          position: 'fixed', 
          left: sidebarOpen ? { xs: '100%', sm: 250 } : 0,
          top: '50%', 
          transform: 'translateY(-50%)', 
          zIndex: 1000,
          bgcolor: 'white',
          borderRadius: '50%',
          boxShadow: 3,
          '&:hover': {
            bgcolor: '#f0f0f0',
          },
        }}
      >
        {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
      </IconButton>

      <DashboardContainer>
        <DashboardHeader>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', textAlign: 'center' }}>
            Dashboard
          </Typography>
         
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <StatusBanner 
              severity={getCurrentMonthStatus() === 'Green' ? 'success' : getCurrentMonthStatus() === 'Yellow' ? 'warning' : 'error'} 
              status={getCurrentMonthStatus()}
              sx={{ 
                width: 'fit-content',
                maxWidth: '80%',
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
                Hi, {unitNumber}! Your current status for this month is: {getCurrentMonthStatus()}
              </Typography>
            </StatusBanner>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 1, 
            mb: 3,
            flexWrap: 'wrap',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            px: 1
          }}>
            <PrimaryButton
              variant="contained"
              startIcon={<FaPhone />}
              href="tel:443-692-5916"
              size="large"
              sx={{ 
                minWidth: '140px',
                maxWidth: '200px',
                width: '100%',
                px: 2,
                fontSize: '0.875rem',
                '& .MuiButton-startIcon': {
                  marginRight: '4px'
                }
              }}
            >
              Call RMA
            </PrimaryButton>

            <SecondaryButton
              variant="contained"
              startIcon={<FaHospital />}
              href="https://edas.miemss.org/dashboard"
              target="_blank"
              size="large"
              sx={{ 
                minWidth: '140px',
                maxWidth: '200px',
                width: '100%',
                px: 2,
                fontSize: '0.875rem',
                '& .MuiButton-startIcon': {
                  marginRight: '4px'
                }
              }}
            >
              Hospital Status (EDAS)
            </SecondaryButton>
          </Box>
        </DashboardHeader>

        <Grid container spacing={1} sx={{ 
          width: '100%', 
          maxWidth: '100%', 
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}>
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard onClick={() => handleCardClick('calls')} sx={{ cursor: 'pointer' }}>
              <StyledCardContent>
                <IconWrapper>
                  <GiAmbulance />
                </IconWrapper>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  Current Points for Calls
                </Typography>
                <Typography variant="h3" color="white" sx={{ fontWeight: 700, my: 2 }}>
                  {callPoints}
                </Typography>
                <SecondaryButton 
                  variant="contained" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenCallDialog(true);
                  }}
                  size="medium"
                >
                  Log New Call
                </SecondaryButton>
              </StyledCardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard onClick={() => handleCardClick('skills')} sx={{ cursor: 'pointer' }}>
              <StyledCardContent>
                <IconWrapper>
                  <FaHandsHelping />
                </IconWrapper>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  Current Points for Skills
                </Typography>
                <Typography variant="h3" color="white" sx={{ fontWeight: 700, my: 2 }}>
                  {skillPoints}
                </Typography>
                <SecondaryButton 
                  variant="contained" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenSkillDialog(true);
                  }}
                  size="medium"
                >
                  Log New Skill
                </SecondaryButton>
              </StyledCardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard onClick={() => handleCardClick('classes')} sx={{ cursor: 'pointer' }}>
              <StyledCardContent>
                <IconWrapper>
                  <GiTeacher />
                </IconWrapper>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  Current Points for Class
                </Typography>
                <Typography variant="h3" color="white" sx={{ fontWeight: 700, my: 2 }}>
                  {classPoints}
                </Typography>
                <SecondaryButton 
                  variant="contained" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenClassDialog(true);
                  }}
                  size="medium"
                >
                  Log New Class
                </SecondaryButton>
              </StyledCardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard onClick={() => handleCardClick('volunteer')} sx={{ cursor: 'pointer' }}>
              <StyledCardContent>
                <IconWrapper>
                  <FaUserClock />
                </IconWrapper>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  Current Volunteer Hours
                </Typography>
                <Typography variant="h3" color="white" sx={{ fontWeight: 700, my: 2 }}>
                  {volunteerHours}
                </Typography>
                <SecondaryButton 
                  variant="contained" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenVolunteerDialog(true);
                  }}
                  size="medium"
                >
                  Log Volunteer Hours
                </SecondaryButton>
              </StyledCardContent>
            </StyledCard>
          </Grid>
        </Grid>

        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', textAlign: 'center' }}>
            Year in Review
          </Typography>
          <Divider sx={{ my: 3, opacity: 0.2 }} />
        </Box>

        <MonthlyTilesContainer container spacing={1} sx={{ 
          width: '100%', 
          maxWidth: '100%', 
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}>
          {renderMonthlyTiles()}
        </MonthlyTilesContainer>

        <StyledDialog 
          open={openCallDialog} 
          onClose={() => setOpenCallDialog(false)} 
          fullWidth 
          maxWidth="sm"
        >
          <DialogTitle>Log a Call</DialogTitle>
          <DialogContent>
            <form onSubmit={handleCallSubmit}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ mb: 2, width: 'calc(100% - 32px)' }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={role}
                  onChange={handleRoleChange}
                  required
                >
                  {roles.map((roleName, index) => (
                    <MenuItem key={index} value={roleName}>{roleName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DialogActions>
                <Button onClick={() => setOpenCallDialog(false)} variant="text">Cancel</Button>
                <PrimaryButton type="submit">Submit</PrimaryButton>
              </DialogActions>
            </form>
          </DialogContent>
        </StyledDialog>

        <StyledDialog 
          open={openConfirmDialog} 
          onClose={() => setOpenConfirmDialog(false)}
        >
          <DialogTitle>Confirm Submission</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography>Did you submit the call in emeds?</Typography>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setOpenConfirmDialog(false)} variant="text">Cancel</Button>
            <PrimaryButton onClick={handleConfirmSubmit}>Yes</PrimaryButton>
          </DialogActions>
        </StyledDialog>

        <StyledDialog 
          open={openBusDialog} 
          onClose={() => setOpenBusDialog(false)}
        >
          <DialogTitle>Bus Information</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <TextField
              autoFocus
              margin="dense"
              label="Bus Number"
              type="text"
              fullWidth
              variant="outlined"
              value={busNumber}
              onChange={(e) => setBusNumber(e.target.value)}
            />
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setOpenBusDialog(false)} variant="text">Cancel</Button>
            <PrimaryButton onClick={handleBusSubmit}>Submit</PrimaryButton>
          </DialogActions>
        </StyledDialog>

        <StyledDialog 
          open={openSkillDialog} 
          onClose={() => setOpenSkillDialog(false)} 
          fullWidth 
          maxWidth="sm"
        >
          <DialogTitle>Log a Skill</DialogTitle>
          <DialogContent>
            <form onSubmit={handleSkillSubmit}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={skillDate}
                onChange={(e) => setSkillDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ mb: 2, width: 'calc(100% - 32px)' }}
              />
              <TextField
                fullWidth
                label="Skill Name"
                value={skillName}
                onChange={(e) => setSkillName(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <Autocomplete
                options={providers}
                onChange={(event, newValue) => setSelectedProvider(newValue)}
                renderInput={(params) => <TextField {...params} label="Select Provider" />}
                sx={{ mb: 2 }}
              />
              <Typography gutterBottom>Practice Hours</Typography>
              <Slider
                value={practiceHours}
                onChange={(e, newValue) => setPracticeHours(newValue)}
                step={0.5}
                min={0.5}
                max={3}
                valueLabelDisplay="auto"
              />
              <DialogActions>
                <Button onClick={() => setOpenSkillDialog(false)} variant="text">Cancel</Button>
                <PrimaryButton type="submit">Submit</PrimaryButton>
              </DialogActions>
            </form>
          </DialogContent>
        </StyledDialog>

        <StyledDialog 
          open={openClassDialog} 
          onClose={() => setOpenClassDialog(false)} 
          fullWidth 
          maxWidth="sm"
        >
          <DialogTitle>Log a Class</DialogTitle>
          <DialogContent>
            <form onSubmit={handleClassSubmit}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={classDate}
                onChange={(e) => setClassDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ mb: 2, width: 'calc(100% - 32px)' }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Class</InputLabel>
                <Select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  required
                >
                  {classes.map((className, index) => (
                    <MenuItem key={index} value={className}>{className}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DialogActions>
                <Button onClick={() => setOpenClassDialog(false)} variant="text">Cancel</Button>
                <PrimaryButton type="submit">Submit</PrimaryButton>
              </DialogActions>
            </form>
          </DialogContent>
        </StyledDialog>

        <StyledDialog 
          open={openVolunteerDialog} 
          onClose={() => setOpenVolunteerDialog(false)} 
          fullWidth 
          maxWidth="sm"
        >
          <DialogTitle>Log Volunteer Hours</DialogTitle>
          <DialogContent>
            <form onSubmit={handleVolunteerSubmit}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={volunteerDate}
                onChange={(e) => setVolunteerDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ mb: 2, width: 'calc(100% - 32px)' }}
              />
              <TextField
                fullWidth
                label="What did you volunteer doing?"
                value={volunteerActivity}
                onChange={(e) => setVolunteerActivity(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <Typography gutterBottom>Volunteer Time (Hours)</Typography>
              <Slider
                value={volunteerTime}
                onChange={(e, newValue) => setVolunteerTime(newValue)}
                step={0.5}
                min={0.5}
                max={4}
                valueLabelDisplay="auto"
              />
              <DialogActions>
                <Button onClick={() => setOpenVolunteerDialog(false)} variant="text">Cancel</Button>
                <PrimaryButton type="submit">Submit</PrimaryButton>
              </DialogActions>
            </form>
          </DialogContent>
        </StyledDialog>

        <StyledDialog 
          open={openDetailsDialog} 
          onClose={handleCloseDetails}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{detailsTitle}</DialogTitle>
          <DialogContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    {detailsType === 'calls' && <TableCell>Role</TableCell>}
                    {detailsType === 'skills' && (
                      <>
                        <TableCell>Skill Name</TableCell>
                        <TableCell>Practice Partner</TableCell>
                        <TableCell>Practice Hours</TableCell>
                      </>
                    )}
                    {detailsType === 'classes' && <TableCell>Class Name</TableCell>}
                    {detailsType === 'volunteer' && (
                      <>
                        <TableCell>Activity</TableCell>
                        <TableCell>Hours</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailsData.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell>{entry.Date}</TableCell>
                      {detailsType === 'calls' && <TableCell>{entry.Role}</TableCell>}
                      {detailsType === 'skills' && (
                        <>
                          <TableCell>{entry.SkillName}</TableCell>
                          <TableCell>{entry.PracticePartner}</TableCell>
                          <TableCell>{entry.PracticeHours}</TableCell>
                        </>
                      )}
                      {detailsType === 'classes' && <TableCell>{entry.ClassName}</TableCell>}
                      {detailsType === 'volunteer' && (
                        <>
                          <TableCell>{entry.Activity}</TableCell>
                          <TableCell>{entry.Time}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetails}>Close</Button>
          </DialogActions>
        </StyledDialog>

        <StyledDialog 
          open={openPieChartDialog} 
          onClose={handleClosePieChart}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedMonth === 'Current Month' ? 'Call Role Distribution' : `Activity Distribution for ${selectedMonth}`}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowChart(!showChart);
                  setSelectedChartSection(null);
                }}
                sx={{ minWidth: 120 }}
              >
                {showChart ? 'Show Table' : 'Show Chart'}
              </Button>
            </Box>
            
            {showChart ? (
              <Box sx={{ height: 400, width: '100%', mb: 4 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={150}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      onClick={handlePieChartClick}
                      isAnimationActive
                      animationDuration={800}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={COLORS[index % COLORS.length]}
                          style={{ cursor: 'pointer' }}
                          stroke="#ffffff"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name) => [`${value} ${selectedMonth === 'Current Month' ? 'calls' : 'points'}`, name]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      {selectedMonth === 'Current Month' ? (
                        <TableCell>Role</TableCell>
                      ) : (
                        <>
                          <TableCell>Calls</TableCell>
                          <TableCell>Skills</TableCell>
                          <TableCell>Classes</TableCell>
                          <TableCell>Volunteer</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedMonth === 'Current Month' ? (
                      detailsData
                        .filter(entry => !selectedChartSection || entry.Role === selectedChartSection)
                        .map((entry, index) => (
                          <TableRow key={index}>
                            <TableCell>{entry.Date}</TableCell>
                            <TableCell>{entry.Role}</TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell>{selectedMonth}</TableCell>
                        <TableCell>{pieChartData.find(d => d.name === 'Calls')?.value || 0}</TableCell>
                        <TableCell>{pieChartData.find(d => d.name === 'Skills')?.value || 0}</TableCell>
                        <TableCell>{pieChartData.find(d => d.name === 'Classes')?.value || 0}</TableCell>
                        <TableCell>{pieChartData.find(d => d.name === 'Volunteer')?.value || 0}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePieChart}>Close</Button>
          </DialogActions>
        </StyledDialog>

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <MuiAlert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ 
              width: '100%',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            }}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </DashboardContainer>
    </Box>
  );
};

export default Dashboard;
